/**
 * Mount once (App). Replays the offline outbox whenever the device comes
 * back online, returns to the foreground, or on a slow interval while
 * anything is queued — then refreshes the task queries the writes touched.
 */
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOutboxStore } from '@/lib/offline/outbox'
import { usePunchQueueStore } from '@/lib/offline/punch-queue'
import { useAuthStore } from '@/stores/auth-store'

const RETRY_INTERVAL_MS = 30_000

const TOUCHED_KEYS = ['work-order-tasks', 'crew-portal-task', 'crew-portal-week', 'crew-portal-wo', 'work-orders']
/** Refreshed after queued punches land, so the tile leaves its queued state. */
const PUNCH_KEYS = ['crew-active-entry', 'crew-time-entries']

export function useOutboxSync() {
  const queryClient = useQueryClient()
  const flush = useOutboxStore((s) => s.flush)
  const setOnline = useOutboxStore((s) => s.setOnline)
  const flushPunches = usePunchQueueStore((s) => s.flush)
  const pendingCount = useOutboxStore((s) => s.entries.length)
  const pendingPunches = usePunchQueueStore((s) => s.entries.length)
  const hasSession = useAuthStore((s) => !!s.session)

  useEffect(() => {
    const run = async () => {
      const { sent } = await flush()
      if (sent > 0) {
        for (const key of TOUCHED_KEYS) {
          queryClient.invalidateQueries({ queryKey: [key], refetchType: 'active' })
        }
      }
      // Punches replay on the same triggers but through their own ordered
      // queue -- a dropped poison entry still counts as progress worth
      // refreshing, so this checks both counters.
      const punch = await flushPunches()
      if (punch.sent > 0 || punch.failed > 0) {
        for (const key of PUNCH_KEYS) {
          queryClient.invalidateQueries({ queryKey: [key], refetchType: 'active' })
        }
      }
    }
    const onOnline = () => { setOnline(true); void run() }
    const onOffline = () => setOnline(false)
    const onVisible = () => { if (!document.hidden) void run() }

    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    document.addEventListener('visibilitychange', onVisible)
    setOnline(navigator.onLine !== false)
    if (hasSession) void run()

    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [flush, setOnline, hasSession, queryClient])

  useEffect(() => {
    if ((pendingCount === 0 && pendingPunches === 0) || !hasSession) return
    const t = window.setInterval(() => {
      void flush()
      void flushPunches()
    }, RETRY_INTERVAL_MS)
    return () => window.clearInterval(t)
  }, [pendingCount, pendingPunches, hasSession, flush, flushPunches])
}
